"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";

import { LOGIN_AS_MODE, LOGIN_AS_UI_MESSAGES } from "@/constants/login-as";
import { refreshLoginAsSession } from "@/lib/auth/refresh-login-as-session";

const postLoginAs = async (path: string, body?: Record<string, string>) => {
  const response = await fetch(path, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    throw new Error(LOGIN_AS_UI_MESSAGES.REQUEST_FAILED);
  }
};

export const LoginAsFab = () => {
  const { data: session, update } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session?.user?.isImpersonating) {
    return null;
  }

  const target = session.user.impersonatedUser;
  const targetLabel =
    target?.email || target?.name || target?.dbUserId || LOGIN_AS_UI_MESSAGES.FALLBACK_TARGET_LABEL;
  const isEditMode = session.user.loginAsMode === LOGIN_AS_MODE.EDIT;

  const refresh = async () => {
    await refreshLoginAsSession(update);
  };

  const onToggleMode = async () => {
    setBusy(true);
    setError(null);
    try {
      await postLoginAs("/api/login-as/mode", {
        mode: isEditMode ? LOGIN_AS_MODE.VIEW : LOGIN_AS_MODE.EDIT,
      });
      await refresh();
    } catch {
      setError(LOGIN_AS_UI_MESSAGES.UNABLE_TO_CHANGE_MODE);
    } finally {
      setBusy(false);
    }
  };

  const onClear = async () => {
    setBusy(true);
    setError(null);
    try {
      await postLoginAs("/api/login-as/clear");
      await refresh();
    } catch {
      setError(LOGIN_AS_UI_MESSAGES.UNABLE_TO_EXIT);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[1000] w-[min(100vw-2rem,22rem)] rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-lg">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
        {isEditMode ? LOGIN_AS_UI_MESSAGES.EDITING_AS : LOGIN_AS_UI_MESSAGES.VIEWING_AS}
      </p>
      <p className="mt-1 truncate text-sm font-medium text-amber-950">{targetLabel}</p>
      {error ? <p className="mt-2 text-xs text-red-700">{error}</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onToggleMode()}
          className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {isEditMode ? LOGIN_AS_UI_MESSAGES.RETURN_TO_VIEW : LOGIN_AS_UI_MESSAGES.ENABLE_EDIT}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void onClear()}
          className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
        >
          {LOGIN_AS_UI_MESSAGES.EXIT}
        </button>
      </div>
    </div>
  );
};
